// Phase 2 — Types for the new video Director Creative draft flow.
//
// These types mirror the Kotlin DTOs in ecommerce-service:
//   - CreateVideoDraftRequest.kt
//   - VideoDraftResponse.kt
//
// The flow:
//   1. POST /ads/videos/draft (CreateVideoDraftRequest) → server returns VideoDraftResponse
//      with status="directing" and a reference_id
//   2. Frontend polls GET /ads/videos/draft/{ref} every 5s
//   3. When status="ready_for_review", show the preview screen
//   4. User clicks Aprobar → POST /draft/{ref}/approve → credits are charged and rendering starts
//   5. OR closes the preview → client discards local draft state; no credits were charged

/**
 * Phase 2 V3 — Audio generation modes for modeling / voice-over drafts.
 *   AUDIO_MODE.PROFESSIONAL — ElevenLabs voz curada + sliders (recomendado)
 *   AUDIO_MODE.OWN          — el user sube / graba su propia voz
 *   AUDIO_MODE.FAST         — Kling genera audio al vuelo con el video
 */
export const AUDIO_MODE = {
  PROFESSIONAL: "professional",
  OWN: "own",
  FAST: "fast",
} as const;

export type AudioMode = (typeof AUDIO_MODE)[keyof typeof AUDIO_MODE];

export interface VideoProductAngle {
  id: string;
  name: string;
  description: string;
  proof?: string | null;
  badge?: string | null;
  source?: string | null;
}

export interface VideoProductContextResponse {
  product_id: string;
  website_id?: string | null;
  product_name: string;
  product_description: string;
  product_image_url?: string | null;
  default_angle?: VideoProductAngle | null;
  angles: VideoProductAngle[];
  source: string;
}

export interface CreateVideoDraftRequest {
  product_id?: string;
  style_id: string; // 'animated-problem' | 'sassy-object' | ...
  duration: number; // 5 | 10 | 15 | 30
  language: string;

  /** Optional sale angle, comes from a Narrative if it exists. */
  sale_angle_name?: string;
  sale_angle_description?: string;

  /** Optional target audience, sent by the frontend. */
  target_audience_description?: string;
  target_audience_vibe?: string;

  /** Optional free-text creative direction. */
  user_instruction?: string;

  /** Phase 6: UGC avatar configuration from the sidebar. */
  avatar_config?: {
    gender?: string;
    age_range?: string;
    skin_tone?: string;
    hair?: string;
    hair_color?: string;
    vibe?: string;
    setting?: string;
    custom_prompt?: string;
    recommendation_id?: string;
    recommendation_role?: string;
    recommendation_label?: string;
    recommendation_fit_reason?: string;
    recommendation_preview_url?: string;
    image_provider?: string;
    image_model?: string;
    source?: string;
  } | null;

  /** Phase 2: voice-over configuration for modeling videos. */
  voice_config?: {
    type: "tts" | "uploaded" | "none";
    voice_id?: string;
    voice_name?: string;
    speed?: number;
    stability?: number;
    similarity_boost?: number;
    style?: number;
    language_code?: string;
    uploaded_audio_url?: string;
  } | null;

  /** Optional override of the agent_id (advanced). */
  agent_id_override?: string;

  /**
   * Phase 2 V2 — If the user already committed a portrait in the Avatar
   * Wizard, pass its URL so the backend skips its own Gemini Image
   * regeneration step. Saves ~20s of preview latency + 1 Gemini credit,
   * and guarantees the picture the user saw is the picture they get.
   */
  pre_generated_base_image_url?: string | null;

  /**
   * Phase 2 V3 — audio generation mode for modeling / voice-over drafts.
   *   "professional" — ElevenLabs curated voice + sliders (recommended)
   *   "own"          — user uploads or records their own voice
   *   "fast"         — Kling generates audio inline with the video
   * Ignored for non-modeling styles.
   */
  audio_mode?: AudioMode | null;

}

/**
 * Mirror of the Kotlin VideoDraftResponse.
 *
 * Status values (from VideoState enum):
 *   - "directing"          → director is running on conversation-engine
 *   - "media_generating"   → reserved for Phase 3
 *   - "ready_for_review"   → director_payload populated, ready for user
 *   - "approved"           → user approved (still ready_for_review state, approved_at != null)
 *   - "rejected"           → user rejected/discarded before approval
 *   - "error"              → director failed before approval
 *   - "rendering"          → Phase 3 (after approve, FAL is rendering)
 *   - "finished"           → Phase 3 (final video ready)
 */
export interface VideoDraftResponse {
  reference_id: string;
  status: string;
  style_id?: string;
  duration?: number;
  language?: string;
  product_id?: string;
  product_name?: string;
  product_image_url?: string;
  credits_charged?: number;
  director_payload?: VideoDirectorPayload;
  error_message?: string;
  /** Phase 5.2: imagen base generada post-approve. Se popula cuando state pasa a waiting_scenes. */
  base_image_url?: string | null;
  /** Optional second generated image for multi-scene previews. */
  end_image_url?: string | null;
  /** Phase 2: voice-over audio URL (TTS or user-uploaded). */
  audio_url?: string | null;
  /** Phase 2 V3: which audio-mode the user chose. Controls preview panel
   *  (voice library | uploader | nothing) and backend dispatch branch. */
  audio_mode?: AudioMode | null;
  /** Phase 2 V2: persisted voice-over config (voice_id, speed, stability,
   *  etc). Sent by createDraft and updated by each generate-audio call so
   *  the PreviewApproval can rehydrate the sliders on modal reopen. */
  voice_config?: {
    type?: string;
    voice_id?: string;
    voice_name?: string;
    speed?: number;
    stability?: number;
    language_code?: string;
    uploaded_audio_url?: string;
  } | null;
  /** Reference images used by the render pipeline. UGC and B-roll
   * voice-over drafts can expose multiple images here for preview/approval. */
  reference_images?: string[] | null;
  /** Phase 5.2: video final mergeado. Se popula cuando state pasa a finished. */
  video_url?: string | null;
  /** Phase 5.4: cuántas regeneraciones de imagen/estilo lleva este draft. */
  regenerate_count?: number;
  /** Phase 5.4: tope de regeneraciones permitidas (3). Cuando regenerate_count llega a este valor, los botones se deshabilitan. */
  max_regenerate_count?: number;
  created_at?: string;
  updated_at?: string;
  approved_at?: string;
  rejected_at?: string;
}

export type VideoDraftRenderPayloadPreview = Record<string, unknown>;

/**
 * Phase 5.4 follow-up: body opcional para POST /draft/{ref}/regenerate-image.
 *
 * Si se manda `instruction`, el backend lo APPENDEA al concept_visual_brief
 * existente como contexto adicional para el image generator. NO reemplaza
 * el wrapper Pixar character ni el resto del prompt — es solo una capa
 * extra de personalización opcional. Si no se manda nada, el endpoint se
 * comporta exactamente como antes (regen con el mismo brief).
 */
export type RegenerateDraftImageTarget =
  | "portrait"
  | "scene_a"
  | "scene_b"
  | "problem_avatar"
  | "integrated_action"
  | "avatar_product"
  | "clean_product"
  | "usage_pov"
  | "all";

export interface RegenerateDraftImageRequest {
  instruction?: string | null;
  /**
   * Phase 6 v2 — UGC drafts have multiple base images. The frontend can
   * target a specific one to regenerate without rolling all of them.
   *   - "portrait": regen the portrait + propagate to scene_a + scene_b
   *     (because they're chained from it)
   *   - "scene_a" / "scene_b": regen only that scene image
   *   - "all": regen everything from scratch (legacy default)
   * Ignored for Kling drafts (sassy, animated) — they only have one image.
   */
  target?: RegenerateDraftImageTarget | null;
}

/** Phase 5.4: body para POST /draft/{ref}/edit-script */
export interface EditDraftScriptRequest {
  script_part_a: string;
  script_part_b?: string | null;
}

/**
 * Phase 2 V3 / V4b — body para POST /draft/{ref}/set-audio-mode.
 * Usado por el preview para cambiar entre "professional" y "own" AFTER
 * el draft ya fue creado. El "fast" mode NO puede setearse via este
 * endpoint — para fast el draft tiene que crearse así desde el sidebar
 * (Kling audio inline).
 */
export interface SetDraftAudioModeRequest {
  audio_mode: "professional" | "own";
  /**
   * Solo requerido cuando audio_mode === "own". URL S3 del archivo subido.
   * Backend field name is `audio_url` (Jackson @JsonProperty on
   * SetDraftAudioModeRequest.kt line 44).
   */
  audio_url?: string | null;
}

/** Phase 5.4: body para POST /draft/{ref}/regenerate-style */
export interface RegenerateDraftStyleRequest {
  pattern_key?: string | null;
  /**
   * Optional natural-language hint for the director ("más enojado",
   * "menos dramático", "más sarcástico"). The backend passes it as
   * user_instruction to the LLM. If null/empty, the director picks
   * a different pattern at random.
   */
  instruction?: string | null;
}

/**
 * Mirror of the Pydantic VideoStudioDraftReadyPayload from conversation-engine.
 *
 * This is what the LLM director returns. The frontend reads it from
 * draft.director_payload to render the preview.
 */
export interface VideoDirectorPayload {
  selected_pattern_key: string;
  selection_reasoning: string;
  concept_visual_brief: string;
  script_part_a: string;
  script_part_b?: string | null;
  ends_with_product_name: boolean;
  cinematic_camera_a: string;
  cinematic_camera_b?: string | null;
  cinematic_prompt_a: string;
  cinematic_prompt_b?: string | null;
  viral_hook_first_3_seconds: string;

  // Phase 6 — UGC fields. Optional and mutually exclusive with the
  // Kling cinematic_* fields. Populated only when style_id === "ugc-testimonial".
  ugc_avatar_visual_brief?: string | null;
  ugc_product_setup_brief?: string | null;
  ugc_scene_a_description?: string | null;
  ugc_scene_b_description?: string | null;
  ugc_voice_tone?: string | null;
  ugc_voice_pace?: string | null;

  // Phase 2 V2 — Modeling voice-over fields. Populated only when
  // style_id === "product-modeling" or "product-modeling-voiceover".
  // script_beat_* are the short spoken blocks aligned to visual beats.
  // Legacy modeling drafts use 3/4 beats; v19 30s voice-over scripts can
  // carry up to 8 micro-beats for problem -> proof -> result -> CTA pacing.
  modeling_scene_brief?: string | null;
  kling_animation_prompt?: string | null;
  modeling_arc?: Array<{ timing: string; action: string; emotion: string }> | null;
  script_beat_1?: string | null;
  script_beat_2?: string | null;
  script_beat_3?: string | null;
  script_beat_4?: string | null;
  script_beat_5?: string | null;
  script_beat_6?: string | null;
  script_beat_7?: string | null;
  script_beat_8?: string | null;
}

/**
 * Phase 2 V2 — One entry of the curated voice library for modeling
 * voice-over. Mirror of Kotlin CuratedVoice.
 */
export interface CuratedVoice {
  voice_id: string;
  display_name: string;
  gender: "female" | "male" | string;
  age_range: string;
  tone: string;
  description: string;
  sample_url?: string | null;
  language_tags: string[];
  recommended_for: string[];
  // Phase 2 V5 (Apr 18 2026) — multi-provider fields. Default provider is
  // "gcloud" (Google Cloud TTS Chirp3-HD); ElevenLabs voices are kept in
  // the backend catalog with provider="eleven" + hidden=true as Plan B.
  // Frontend receives the visible ones only, so `hidden` never arrives as
  // true in practice — kept here for type symmetry and future debug tools.
  provider?: "gcloud" | "eleven" | "gemini";
  style_prompt?: string | null;
  default_settings?: {
    stability: number;
    similarity_boost: number;
    style: number;
    speed: number;
  } | null;
  fallback_gemini_voice_id?: string | null;
  hidden?: boolean;
}

// Phase 2 V7 (Apr 19 2026) — user-authored custom voices
export interface PreviewCustomVoiceRequest {
  description: string;
}

export interface PreviewCustomVoiceResponse {
  base_voice: string;
  style_prompt: string;
  suggested_display_name: string;
  sample_url: string;
  reasoning: string;
}

export interface CreateCustomVoiceRequest {
  display_name: string;
  description: string;
  base_voice: string;
  style_prompt: string;
  sample_url?: string | null;
}

export interface UpdateCustomVoiceRequest {
  display_name: string;
}

export interface CustomVoiceResponse {
  voice_id: string;            // "custom-<dbId>"
  display_name: string;
  description: string;
  base_voice: string;
  style_prompt: string;
  sample_url?: string | null;
  usage_count: number;
  last_used_at?: string | null;
  created_at: string;
}

/**
 * Phase 2 V2 — Request body for POST /ads/videos/draft/{ref}/generate-audio.
 * Triggered from the voice picker after the user is happy with the script
 * and chose a voice.
 */
export interface GenerateDraftAudioRequest {
  voice_id: string;
  voice_name?: string;
  speed?: number;
  stability?: number;
  /** Phase 2 V2 Fase 4 — ElevenLabs similarity_boost (0-1). */
  similarity_boost?: number;
  /** Phase 2 V2 Fase 4 — ElevenLabs style exaggeration (0-1). */
  style?: number;
  language_code?: string;
  /** User-edited script (beats joined with \n\n). Omit to use the director's original beats. */
  script_override?: string;
}

/**
 * Phase 2 V2 — Live avatar preview for the wizard. The frontend debounces
 * changes to avatar_config and hits this to refresh the portrait.
 */
export interface AvatarPreviewRequest {
  avatar_config: {
    gender?: string;
    age_range?: string;
    skin_tone?: string;
    hair?: string;
    hair_color?: string;
    vibe?: string;
    setting?: string;
  };
  product_image_url?: string | null;
  product_name?: string | null;
}

export interface AvatarPreviewResponse {
  preview_url: string;
}

export interface AvatarRecommendationSaleAngle {
  name?: string | null;
  description?: string | null;
}

export interface AvatarRecommendationVoice {
  voice_id?: string | null;
  voice_name?: string | null;
  display_name?: string | null;
  gender?: string | null;
  accent?: string | null;
}

export interface AvatarRecommendationsRequest {
  product_id: string;
  website_id?: string | null;
  style_id?: string;
  language?: string;
  locale?: string | null;
  country?: string | null;
  sale_angle?: AvatarRecommendationSaleAngle | null;
  selected_voice?: AvatarRecommendationVoice | null;
  force_regenerate?: boolean;
  client_generation_id?: string | null;
}

export interface AvatarRecommendationItem {
  id: string;
  role: "problem_fit" | "routine_fit" | "aspiration_fit" | "trust_fit" | string;
  display_name: string;
  label: string;
  preview_url: string;
  fit_reason: string;
  avatar_config: Record<string, unknown>;
  provider: string;
  model: string;
  confidence?: number | null;
}

export interface AvatarRecommendationsResponse {
  product_id: string;
  website_id?: string | null;
  source: "cache" | "ai" | "partial" | "disabled" | string;
  recommendations: AvatarRecommendationItem[];
}

/**
 * Phase 2 V2 — Fase C. Row shape of /ads/videos/avatar/mine GET.
 * Mirror of Kotlin UserAvatar entity. `avatar_config` comes down as a
 * JSON string from the backend (JSONB column). The hook parses it client-
 * side so the wizard can re-hydrate the builder form from a saved row.
 */
export interface SavedAvatar {
  id: number;
  owner_id: string;
  display_name: string;
  thumbnail_url: string;
  avatar_config: string;
  source: "preset" | "custom" | "upload" | string;
  created_at: string;
  updated_at: string;
}

export interface SaveAvatarRequest {
  display_name: string;
  thumbnail_url: string;
  avatar_config: {
    gender?: string;
    age_range?: string;
    skin_tone?: string;
    hair?: string;
    hair_color?: string;
    vibe?: string;
    setting?: string;
    photographic_style?: string;
    skin_finish?: string;
    custom_prompt?: string;
    recommendation_id?: string;
    recommendation_role?: string;
    recommendation_label?: string;
    recommendation_fit_reason?: string;
    recommendation_preview_url?: string;
    image_provider?: string;
    image_model?: string;
    source?: string;
  };
  source?: "preset" | "custom" | "upload" | "prompt" | "recommended";
}

/** Frontend-only state machine of the draft for the UI. Mirror of VideoState.kt externalValue. */
export const VIDEO_DRAFT_STATUS = {
  DIRECTING: "directing",
  MEDIA_GENERATING: "media_generating",
  READY_FOR_REVIEW: "ready_for_review",
  REJECTED: "rejected",
  ERROR: "error",
  // Post-approve states (Phase 3 dispatch + legacy FAL pipeline)
  GENERATING_IMAGE: "generating_image",
  WAITING_SCENES: "waiting_scenes",
  MERGING: "merging",
  FINISHED: "finished",
} as const;

export type VideoDraftStatus = (typeof VIDEO_DRAFT_STATUS)[keyof typeof VIDEO_DRAFT_STATUS];
